/**
 * StackedBar (internal, used by SplitBar and DurationBar): one horizontal bar cut into 2-4 parts.
 * Each part has its own pattern (solid, stripes, dots, hatch) and a legend entry, so colour is never the only cue.
 * parts[].text is the legend value; rows/headers feed the hidden table; compact hides legend and footer.
 */
import clsx from 'clsx';
import { ReactNode, useId } from 'react';
import { ChartState, DataTable, Legend, PatternDef } from './chartKit';
import { SURFACE, fillFor, uidOf } from './chartUtils';

export interface StackedPart {
    key: string;
    label: string;
    value: number;
    text: string;
}

interface StackedBarProps {
    caption: string;
    parts: StackedPart[];
    headers: string[];
    rows: string[][];
    compact?: boolean | undefined;
    footer?: ReactNode;
    loading?: boolean | undefined;
    className?: string | undefined;
}

export const StackedBar = ({ caption, parts: all, headers, rows, compact, footer, loading, className }: StackedBarProps) => {
    const uid = uidOf(useId());
    const parts = all.slice(0, 4);
    const total = parts.reduce((s, p) => s + Math.max(p.value, 0), 0);
    const barH = compact ? 6 : 20;
    let offset = 0;
    return (
        <div className={className}>
            <ChartState loading={loading} empty={total <= 0 && !compact} height={compact ? barH : 64}>
                <div aria-hidden="true">
                    <svg width="100%" height={barH} focusable="false" className={clsx('block overflow-hidden', compact ? 'rounded-full' : 'rounded-md')}>
                        <defs>{parts.map((_, i) => <PatternDef key={i} uid={uid} i={i} />)}</defs>
                        {parts.map((p, i) => {
                            const w = total > 0 ? (Math.max(p.value, 0) / total) * 100 : 0;
                            const x = offset;
                            offset += w;
                            return w > 0 ? (
                                <rect key={p.key} x={`${x}%`} width={`${w}%`} height={barH} fill={fillFor(uid, i)} stroke={SURFACE} strokeWidth={compact ? 1 : 2}>
                                    <title>{`${p.label}: ${p.text}`}</title>
                                </rect>
                            ) : null;
                        })}
                    </svg>
                    {!compact && <Legend uid={uid} entries={parts.map((p) => ({ key: p.key, label: p.label, text: p.text }))} />}
                    {!compact && footer}
                </div>
            </ChartState>
            <DataTable caption={caption} headers={headers} rows={rows} />
        </div>
    );
};
